import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Drawer,
  Form,
  Input,
  Select,
  Typography,
  message,
  Modal,
  Collapse,
  Tooltip,
  Divider,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import { Link } from 'react-router-dom';
import { adminApi } from '@/services/api';
import type {
  AdminUser,
  AdminRoleType,
  AdminUserStatus,
  AdminRoleWithPermissions,
  PermissionAction,
  ResourcePermissions,
} from '@/types';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;

const ROLE_OPTIONS: { value: AdminRoleType; label: string }[] = [
  { value: 'super_admin', label: 'Super Admin' },
  { value: 'product_manager', label: 'Product Manager' },
  { value: 'game_designer', label: 'Game Designer' },
  { value: 'liveops_manager', label: 'LiveOps Manager' },
  { value: 'customer_support', label: 'Customer Support' },
  { value: 'analyst', label: 'Analyst' },
];

const ROLE_COLORS: Record<AdminRoleType, string> = {
  super_admin: 'red',
  product_manager: 'blue',
  game_designer: 'purple',
  liveops_manager: 'orange',
  customer_support: 'cyan',
  analyst: 'default',
};

const STATUS_OPTIONS: { value: AdminUserStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' },
];

const STATUS_COLORS: Record<AdminUserStatus, string> = {
  active: 'green',
  inactive: 'default',
  suspended: 'orange',
};

// Mapping from each admin sidebar module to the underlying permission
// resource its controllers gate on. The mapping was derived by grepping
// `@AdminOnly(Permission('<resource>', ...))` decorators across
// wcc4-backend/src/modules/admin/controllers/. Modules whose controller
// is not yet permission-gated (or whose backend resource is not present
// in the role permission JSONB) are marked `resource: null` so the
// matrix can call them out as super-admin-only.
interface SidebarModule {
  label: string;
  resource: keyof ResourcePermissions | null;
}

const SIDEBAR_MODULES: SidebarModule[] = [
  { label: 'Today', resource: 'analytics' },
  { label: 'Players', resource: 'players' },
  { label: 'Analytics', resource: 'analytics' },
  { label: 'AI Analytics', resource: 'analytics' },
  { label: 'LiveOps', resource: 'liveops' },
  { label: 'Cricket Calendar', resource: 'liveops' },
  { label: 'Match Pushes', resource: 'liveops' },
  { label: 'Offers', resource: 'liveops' },
  { label: 'Products', resource: 'economy' },
  { label: 'Purchases', resource: null },
  { label: 'Tournaments', resource: 'tournaments' },
  { label: 'Live Events', resource: 'liveops' },
  { label: 'Config', resource: 'config' },
  { label: 'Webhooks', resource: 'config' },
  { label: 'Player Data', resource: 'player_data' },
  { label: 'Cron Jobs', resource: 'config' },
  { label: 'Diagnostics', resource: 'config' },
  { label: 'Store Sync', resource: 'economy' },
  { label: 'Deeplinks', resource: 'liveops' },
  { label: 'Hot Events', resource: 'liveops' },
  { label: 'Inbox', resource: 'liveops' },
  { label: 'Missions', resource: 'liveops' },
  { label: 'Cohorts', resource: 'economy' },
  { label: 'Admins', resource: 'admins' },
  { label: 'Custom Ads', resource: null },
  { label: 'Daily Deals', resource: 'liveops' },
  { label: 'CM National Teams', resource: null },
];

interface CreateFormShape {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  roleType: AdminRoleType;
}

interface EditFormShape {
  firstName: string;
  lastName: string;
  roleType: AdminRoleType;
  status: AdminUserStatus;
  newPassword?: string;
}

function readCurrentAdminId(): string | null {
  try {
    const raw = localStorage.getItem('admin_user');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { id?: string };
    return parsed.id ?? null;
  } catch {
    return null;
  }
}

function useDebounce<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

const AdminsPage: React.FC = () => {
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [roles, setRoles] = useState<AdminRoleWithPermissions[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [createForm] = Form.useForm<CreateFormShape>();
  const [editForm] = Form.useForm<EditFormShape>();

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [roleFilter, setRoleFilter] = useState<AdminRoleType | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<AdminUserStatus | undefined>(undefined);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 300);
  const [orderBy, setOrderBy] = useState<'createdAt' | 'lastLoginAt' | 'email'>('createdAt');
  const [orderDir, setOrderDir] = useState<'ASC' | 'DESC'>('DESC');

  const currentAdminId = useMemo(() => readCurrentAdminId(), []);
  const isEditingSelf = editingId !== null && editingId === currentAdminId;

  const fetchAdmins = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.listAdmins({
        page: pagination.current,
        limit: pagination.pageSize,
        roleType: roleFilter,
        status: statusFilter,
        search: search.trim() || undefined,
        orderBy,
        orderDir,
      });
      const payload = res.data ?? {};
      setAdmins(payload.data ?? []);
      setPagination((p) => ({ ...p, total: payload.total ?? 0 }));
    } catch (err) {
      console.error(err);
      message.error('Failed to load admin users');
    } finally {
      setLoading(false);
    }
    // Depending on the whole `pagination` object would loop because the
    // fetch updates `total` via setPagination. We only want re-fetch on
    // the user-controlled fields.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pagination.current,
    pagination.pageSize,
    roleFilter,
    statusFilter,
    search,
    orderBy,
    orderDir,
  ]);

  const fetchRoles = useCallback(async () => {
    try {
      const res = await adminApi.listAdminRoles();
      setRoles(res.data ?? []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    void fetchAdmins();
  }, [fetchAdmins]);

  useEffect(() => {
    void fetchRoles();
  }, [fetchRoles]);

  const handleTableChange = (
    pg: TablePaginationConfig,
    _filters: unknown,
    sorter: SorterResult<AdminUser> | SorterResult<AdminUser>[]
  ) => {
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    if (s && s.order && typeof s.field === 'string') {
      const sortable = new Set(['createdAt', 'lastLoginAt', 'email']);
      if (sortable.has(s.field)) {
        setOrderBy(s.field as 'createdAt' | 'lastLoginAt' | 'email');
        setOrderDir(s.order === 'ascend' ? 'ASC' : 'DESC');
      }
    }
    setPagination((p) => ({
      ...p,
      current: pg.current ?? 1,
      pageSize: pg.pageSize ?? 20,
    }));
  };

  const clearFilters = () => {
    setRoleFilter(undefined);
    setStatusFilter(undefined);
    setSearchInput('');
    setPagination((p) => ({ ...p, current: 1 }));
  };

  const openCreate = () => {
    setEditingId(null);
    createForm.resetFields();
    createForm.setFieldsValue({ roleType: 'analyst' });
    setDrawerOpen(true);
  };

  const openEdit = (admin: AdminUser) => {
    setEditingId(admin.id);
    editForm.resetFields();
    editForm.setFieldsValue({
      firstName: admin.firstName,
      lastName: admin.lastName,
      roleType: admin.role.type,
      status: admin.status,
      newPassword: '',
    });
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingId) {
        const values = await editForm.validateFields([
          'firstName',
          'lastName',
          'roleType',
          'status',
        ]);
        await adminApi.updateAdminUser(editingId, {
          firstName: values.firstName,
          lastName: values.lastName,
          roleType: values.roleType,
          status: values.status,
        });
        message.success('Admin updated');
      } else {
        const values = await createForm.validateFields();
        await adminApi.createAdminUser(values);
        message.success(`${values.email} created`);
      }
      setDrawerOpen(false);
      await fetchAdmins();
    } catch (err: unknown) {
      const apiErr = err as {
        response?: { data?: { message?: string | string[] } };
        errorFields?: unknown;
      };
      if (apiErr?.errorFields) return;
      const apiMsg = Array.isArray(apiErr?.response?.data?.message)
        ? apiErr.response.data.message.join(', ')
        : apiErr?.response?.data?.message;
      message.error(apiMsg || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    const newPassword = editForm.getFieldValue('newPassword') as string | undefined;
    if (!newPassword || newPassword.length < 8) {
      message.error('New password must be at least 8 characters');
      return;
    }
    if (!editingId) return;
    Modal.confirm({
      title: 'Reset this admin’s password?',
      content: 'This sets a new password immediately and clears any active login lockout.',
      okText: 'Reset',
      okButtonProps: { danger: true },
      onOk: async () => {
        setResettingPassword(true);
        try {
          await adminApi.resetAdminPassword(editingId, newPassword);
          message.success('Password reset');
          editForm.setFieldValue('newPassword', '');
        } catch (err: unknown) {
          const apiErr = err as {
            response?: { data?: { message?: string | string[] } };
          };
          const apiMsg = Array.isArray(apiErr?.response?.data?.message)
            ? apiErr.response.data.message.join(', ')
            : apiErr?.response?.data?.message;
          message.error(apiMsg || 'Password reset failed');
        } finally {
          setResettingPassword(false);
        }
      },
    });
  };

  const columns: ColumnsType<AdminUser> = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: true,
      sortOrder: orderBy === 'email' ? (orderDir === 'ASC' ? 'ascend' : 'descend') : null,
      render: (v: string, r) => (
        <Space>
          <Text strong>{v}</Text>
          {r.id === currentAdminId && <Tag color="geekblue">You</Tag>}
        </Space>
      ),
    },
    { title: 'Name', dataIndex: 'fullName', key: 'fullName' },
    {
      title: 'Role',
      dataIndex: ['role', 'type'],
      key: 'role',
      width: 180,
      render: (type: AdminRoleType) => (
        <Tag color={ROLE_COLORS[type]}>
          {ROLE_OPTIONS.find((o) => o.value === type)?.label ?? type}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (s: AdminUserStatus) => (
        <Tag color={STATUS_COLORS[s]}>{STATUS_OPTIONS.find((o) => o.value === s)?.label ?? s}</Tag>
      ),
    },
    {
      title: 'Last Login',
      dataIndex: 'lastLoginAt',
      key: 'lastLoginAt',
      width: 160,
      sorter: true,
      sortOrder: orderBy === 'lastLoginAt' ? (orderDir === 'ASC' ? 'ascend' : 'descend') : null,
      render: (v?: string) => (v ? dayjs(v).fromNow() : 'Never'),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 130,
      sorter: true,
      sortOrder: orderBy === 'createdAt' ? (orderDir === 'ASC' ? 'ascend' : 'descend') : null,
      render: (v: string) => dayjs(v).format('YYYY-MM-DD'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, r) => (
        <Space size={0}>
          <Link to={`/admins/${r.id}`}>
            <Button type="link" icon={<EyeOutlined />}>
              View
            </Button>
          </Link>
          <Button type="link" icon={<EditOutlined />} onClick={() => openEdit(r)}>
            Edit
          </Button>
        </Space>
      ),
    },
  ];

  const hasFilters = !!roleFilter || !!statusFilter || !!searchInput;

  // Matrix orientation: rows = sidebar modules (so admins can scan
  // "which features can role X touch"), columns = roles. Super-admin is
  // a no-op gate (the backend bypasses checks for that role type) so its
  // column shows "Full" tags regardless of what the JSONB says.
  const moduleMatrixColumns: ColumnsType<SidebarModule> = [
    {
      title: 'Module',
      dataIndex: 'label',
      key: 'label',
      width: 200,
      fixed: 'left',
      render: (label: string, m) => (
        <Space direction="vertical" size={0}>
          <Text strong>{label}</Text>
          {m.resource ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              gates on <code>{m.resource}</code>
            </Text>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              super-admin only
            </Text>
          )}
        </Space>
      ),
    },
    ...roles.map<ColumnsType<SidebarModule>[number]>((role) => ({
      title: <Tag color={ROLE_COLORS[role.type]}>{role.name}</Tag>,
      key: role.id,
      width: 200,
      render: (_, m) => {
        if (role.type === 'super_admin') {
          return <Tag color="red">Full</Tag>;
        }
        if (!m.resource) {
          return <Text type="secondary">—</Text>;
        }
        const actions = role.permissions?.[m.resource];
        if (!actions || actions.length === 0) {
          return <Text type="secondary">—</Text>;
        }
        return (
          <Space size={2} wrap>
            {actions.map((a: PermissionAction) => (
              <Tag key={a} style={{ marginRight: 0 }}>
                {a}
              </Tag>
            ))}
          </Space>
        );
      },
    })),
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space
        direction="horizontal"
        style={{
          width: '100%',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <div>
          <Title level={3} style={{ marginBottom: 4 }}>
            Admin Users
          </Title>
          <Text type="secondary">
            Manage who has access to this admin panel. To &quot;remove&quot; an admin, edit them and
            set status to <Tag>Inactive</Tag>. You cannot change your own role or status.
          </Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchAdmins}>
            Refresh
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Create Admin
          </Button>
        </Space>
      </Space>

      <Collapse style={{ marginBottom: 16 }} ghost>
        <Collapse.Panel header={<strong>Role Permissions Reference</strong>} key="roles">
          <Text type="secondary">
            Each row is a module visible in the admin sidebar; columns are the available roles. Tags
            show the actions a role can perform on that module&apos;s underlying permission
            resource. Super Admins bypass all checks. Modules marked &quot;super-admin only&quot;
            don&apos;t have a permission gate yet — only super-admins can use them.
          </Text>
          <Table<SidebarModule>
            rowKey="label"
            dataSource={SIDEBAR_MODULES}
            columns={moduleMatrixColumns}
            pagination={false}
            size="small"
            scroll={{ x: 'max-content' }}
            style={{ marginTop: 12 }}
          />
        </Collapse.Panel>
      </Collapse>

      <Card style={{ marginBottom: 16 }} size="small">
        <Space wrap>
          <Input.Search
            placeholder="Search email / first name / last name"
            allowClear
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setPagination((p) => ({ ...p, current: 1 }));
            }}
            style={{ width: 320 }}
          />
          <Select<AdminRoleType | undefined>
            placeholder="All roles"
            value={roleFilter}
            onChange={(v) => {
              setRoleFilter(v);
              setPagination((p) => ({ ...p, current: 1 }));
            }}
            allowClear
            options={ROLE_OPTIONS}
            style={{ width: 200 }}
          />
          <Select<AdminUserStatus | undefined>
            placeholder="All statuses"
            value={statusFilter}
            onChange={(v) => {
              setStatusFilter(v);
              setPagination((p) => ({ ...p, current: 1 }));
            }}
            allowClear
            options={STATUS_OPTIONS}
            style={{ width: 160 }}
          />
          {hasFilters && (
            <Button type="link" onClick={clearFilters}>
              Clear filters
            </Button>
          )}
        </Space>
      </Card>

      <Card>
        <Table<AdminUser>
          rowKey="id"
          loading={loading}
          dataSource={admins}
          columns={columns}
          onChange={handleTableChange}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
          }}
        />
      </Card>

      <Drawer
        title={editingId ? 'Edit Admin' : 'Create Admin'}
        width={640}
        open={drawerOpen}
        onClose={closeDrawer}
        destroyOnClose
        extra={
          <Space>
            <Button onClick={closeDrawer}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={handleSave}>
              Save
            </Button>
          </Space>
        }
      >
        {editingId ? (
          <Form<EditFormShape> form={editForm} layout="vertical">
            <Form.Item label="Email">
              <Input value={admins.find((a) => a.id === editingId)?.email ?? ''} disabled />
            </Form.Item>
            <Form.Item
              label="First Name"
              name="firstName"
              rules={[{ required: true, message: 'Required' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Last Name"
              name="lastName"
              rules={[{ required: true, message: 'Required' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={
                <Space>
                  <span>Role</span>
                  <Tooltip title="See the Role Permissions Reference at the top of the page for what each role can do.">
                    <InfoCircleOutlined style={{ color: '#999' }} />
                  </Tooltip>
                </Space>
              }
              name="roleType"
              rules={[{ required: true, message: 'Required' }]}
              extra={isEditingSelf ? 'Cannot change your own role' : undefined}
            >
              <Select options={ROLE_OPTIONS} disabled={isEditingSelf} />
            </Form.Item>
            <Form.Item
              label="Status"
              name="status"
              rules={[{ required: true, message: 'Required' }]}
              extra={isEditingSelf ? 'Cannot deactivate yourself' : undefined}
            >
              <Select options={STATUS_OPTIONS} disabled={isEditingSelf} />
            </Form.Item>

            <Divider />

            <Card size="small" title="Reset Password">
              <Form.Item
                label="New Password"
                name="newPassword"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      if (value.length < 8) {
                        return Promise.reject(new Error('Minimum 8 characters'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                extra={
                  isEditingSelf
                    ? 'Use the change-password endpoint for your own account.'
                    : 'Sets a new password immediately and clears any login lockout. Share with the admin out-of-band.'
                }
              >
                <Input.Password placeholder="At least 8 characters" disabled={isEditingSelf} />
              </Form.Item>
              <Button
                onClick={handleResetPassword}
                loading={resettingPassword}
                disabled={isEditingSelf}
                danger
              >
                Reset Password
              </Button>
            </Card>
          </Form>
        ) : (
          <Form<CreateFormShape> form={createForm} layout="vertical">
            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: 'Required' },
                { type: 'email', message: 'Invalid email' },
              ]}
            >
              <Input placeholder="admin@wcc4.com" />
            </Form.Item>
            <Form.Item
              label="Password"
              name="password"
              rules={[
                { required: true, message: 'Required' },
                { min: 8, message: 'Minimum 8 characters' },
              ]}
              extra="The new admin will use this to log in. Share securely."
            >
              <Input.Password placeholder="At least 8 characters" />
            </Form.Item>
            <Form.Item
              label="First Name"
              name="firstName"
              rules={[{ required: true, message: 'Required' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Last Name"
              name="lastName"
              rules={[{ required: true, message: 'Required' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={
                <Space>
                  <span>Role</span>
                  <Tooltip title="See the Role Permissions Reference at the top of the page for what each role can do.">
                    <InfoCircleOutlined style={{ color: '#999' }} />
                  </Tooltip>
                </Space>
              }
              name="roleType"
              rules={[{ required: true, message: 'Required' }]}
            >
              <Select options={ROLE_OPTIONS} />
            </Form.Item>
          </Form>
        )}
      </Drawer>
    </div>
  );
};

export default AdminsPage;
